import { ArrowUpRight, Sparkles } from "lucide-react";
import Reveal from "@/components/Reveal";
import { useI18n } from "@/lib/i18n";

const SERVICES = [
  {
    title: "Personal Loan",
    tag: "Up to 15% · 36 mo",
    range: "₹10K – ₹2 Cr",
    desc: "Quick personal funding for weddings, medical, education, travel or consolidation.",
    points: ["No CIBIL required", "No fixed income proof", "18–60 yrs eligibility"],
  },
  {
    title: "Cattle Loan",
    tag: "Pashupalan · up to 15%",
    range: "₹15K – ₹2 Cr",
    desc: "Dairy, livestock & pashupalan funding with vet-supported diligence. 24-month tenure.",
    points: ["Group & individual", "Insurance-linked", "Rural-friendly"],
  },
  {
    title: "Mortgage Loan",
    tag: "Up to 15% · 48 mo",
    range: "₹10K – ₹2 Cr",
    desc: "Loan against residential, commercial or industrial property at competitive rates.",
    points: ["LAP & home top-up", "Balance-transfer", "Tenure up to 48 mo"],
  },
  {
    title: "Housing Loan",
    tag: "Griha · 60 mo",
    range: "₹1L – ₹10 Cr",
    desc: "Home purchase, construction & improvement loans with tax-benefit guidance.",
    points: ["Floating & fixed", "Up to 80% funding", "Tax advisory included"],
  },
  {
    title: "Agriculture Loan",
    tag: "Krishi · 15%",
    range: "₹10K – ₹2 Cr",
    desc: "Crop, equipment and allied-agri loans for farmers and FPOs across rural India.",
    points: ["Seasonal repayment", "Subsidy-linked", "Doorstep verification"],
  },
  {
    title: "Loan against Credit Card",
    tag: "Up to 15% · 24 mo",
    range: "₹10K – ₹5L",
    desc: "Tap idle credit-card limits into instant cash for any short-term need.",
    points: ["Same-day disbursal", "Flexible tenure", "Minimal paperwork"],
  },
  {
    title: "Loan against FD",
    tag: "From 6.50% · 36 mo",
    range: "₹10K onwards",
    desc: "Instant overdraft against your fixed deposit — no liquidation, no penalty.",
    points: ["Pay on usage only", "Keep earning FD interest", "Lowest interest slab"],
  },
  {
    title: "Social-Credit Loan",
    tag: "From 6.5% · 48 mo",
    range: "₹10K – ₹2 Cr",
    desc: "Inclusive lending for first-time borrowers via community-based credit scoring.",
    points: ["No credit history needed", "Microfinance heritage", "JLG & SHG models"],
  },
  {
    title: "Plot Loan",
    tag: "Up to 15% · 60 mo",
    range: "₹1L – ₹5 Cr",
    desc: "Purchase residential or approved-layout plots with structured funding & documentation support.",
    points: ["NA / Non-NA plots", "Legal & title advisory", "Flexible EMI"],
  },
  {
    title: "Loan Against Open Plot",
    tag: "Up to 15% · 48 mo",
    range: "₹1L – ₹5 Cr",
    desc: "Unlock capital from your idle open plot without selling — perfect for expansion or emergencies.",
    points: ["Own the plot? Get funded", "48-month tenure", "Fast title check"],
  },
  {
    title: "Gold Loan",
    tag: "Up to 15% · 24 mo",
    range: "₹10K – ₹1 Cr",
    desc: "Instant liquidity against your gold ornaments with safe custody and transparent pricing.",
    points: ["Same-day disbursal", "Secure locker storage", "Prepay anytime"],
  },
  {
    title: "Gold Loan for Buy",
    tag: "Up to 15% · 36 mo",
    range: "₹25K – ₹1 Cr",
    desc: "Financing to buy hallmarked gold jewellery — pay in easy EMIs while owning the asset.",
    points: ["Direct jeweller tie-ups", "GST-inclusive quote", "Tenure up to 36 mo"],
  },
  {
    title: "Loan Against Security Deposit",
    tag: "From 8% · 48 mo",
    range: "₹25K – ₹2 Cr",
    desc: "Borrow against your rental security deposits, corporate deposits or held instruments.",
    points: ["No CIBIL required", "Transparent LTV", "Quick disbursal"],
  },
  {
    title: "Other Loans",
    tag: "Custom-built",
    range: "Tailored",
    desc: "Project loans, vehicle loans and loans against securities — tailored to need.",
    points: ["Project financing", "Vehicle loans", "Loan against securities"],
  },
];

export default function Services() {
  const { t } = useI18n();
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative bg-[#FAF6EC] text-[#0C1530] py-24 md:py-32 overflow-hidden"
    >
      <div className="pointer-events-none absolute top-0 right-0 w-[460px] h-[460px] rounded-full bg-[#D4AF37]/10 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 left-0 w-[380px] h-[380px] rounded-full bg-[#0A1F44]/5 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <div className="grid md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-7">
              <span className="text-[11px] tracking-[0.3em] uppercase text-[#B68F1E]">
                {t("services.eyebrow")}
              </span>
              <h2
                data-testid="services-heading"
                className="font-display text-[40px] md:text-[60px] mt-3 leading-[1.05] tracking-tight"
              >
                {t("services.heading.line1")} {t("services.heading.line2")}. <br />
                <em className="not-italic text-[#B68F1E]">{t("services.heading.line3")}.</em>
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="text-[#0C1530]/70 leading-relaxed">
                {t("services.sub")}
              </p>
            </div>
          </div>
        </Reveal>

        {/* Services grid */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={i * 50}>
              <article
                data-testid={`service-card-${s.title.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}
                className="group h-full bg-white rounded-2xl p-7 border border-[#0A1F44]/10 hover:border-[#D4AF37]/60 hover:-translate-y-1 transition-all duration-300 shadow-[0_10px_30px_-22px_rgba(10,31,68,0.4)] hover:shadow-[0_20px_50px_-22px_rgba(212,175,55,0.6)] relative overflow-hidden"
              >
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#D4AF37] via-[#F0D788] to-[#D4AF37] scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500" />
                <div className="flex items-start justify-between gap-3">
                  <span className="text-[10px] tracking-[0.22em] uppercase text-[#B68F1E] bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full px-2.5 py-1">
                    {s.tag}
                  </span>
                  <ArrowUpRight
                    size={20}
                    className="text-[#0A1F44]/40 group-hover:text-[#D4AF37] group-hover:rotate-12 transition-all"
                  />
                </div>
                <h3 className="mt-5 font-display text-2xl md:text-[28px] leading-tight">
                  {s.title}
                </h3>
                <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[#0A1F44]/70">
                  <span className="text-[#B68F1E]">●</span>
                  <span>Range: <span className="text-[#0A1F44] font-bold">{s.range}</span></span>
                </div>
                <p className="mt-3 text-[#0C1530]/70 leading-relaxed text-[15px]">{s.desc}</p>
                <ul className="mt-5 space-y-2">
                  {s.points.map((p) => (
                    <li key={p} className="flex items-start gap-2.5 text-sm text-[#0C1530]/80">
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#D4AF37] flex-shrink-0" />
                      {p}
                    </li>
                  ))}
                </ul>
              </article>
            </Reveal>
          ))}
        </div>

        {/* Eligibility band */}
        <Reveal delay={300}>
          <div
            data-testid="services-eligibility"
            className="mt-14 rounded-3xl bg-[#0A1F44] text-white p-7 md:p-10 noise-overlay relative overflow-hidden"
          >
            <div className="pointer-events-none absolute -top-12 -right-12 w-64 h-64 rounded-full bg-[#D4AF37]/15 blur-3xl" />
            <div className="grid md:grid-cols-4 gap-6 items-center">
              <div className="md:col-span-1">
                <span className="text-[11px] tracking-[0.3em] uppercase text-[#D4AF37]">Eligibility</span>
                <h3 className="font-display text-2xl mt-2 leading-tight">Designed for everyone.</h3>
              </div>
              <div className="md:col-span-3 grid sm:grid-cols-3 gap-4">
                {[
                  { k: "18–60 yrs", v: "Age eligibility" },
                  { k: "No CIBIL", v: "Credit score not required" },
                  { k: "No Fixed Income", v: "Salary slip optional" },
                ].map((b) => (
                  <div
                    key={b.k}
                    data-testid={`eligibility-${b.k.toLowerCase().replace(/\s+/g, "-")}`}
                    className="rounded-xl p-4 border border-[#D4AF37]/25 bg-white/[0.04]"
                  >
                    <div className="font-display text-xl text-[#F0D788]">{b.k}</div>
                    <div className="text-white/65 text-xs uppercase tracking-wider mt-1">
                      {b.v}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>

        {/* Bottom note */}
        <Reveal delay={350}>
          <div
            data-testid="services-bottom-note"
            className="mt-10 flex flex-col md:flex-row items-center justify-center gap-3 text-center md:text-left"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0A1F44] text-[#F0D788] text-sm">
              <Sparkles size={14} />
              ROI 6.5% – 15% p.a. (product &amp; risk-profile dependent). Talk to our advisors to find the right product.
            </span>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
